import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
    Tooltip,
    Legend,
} from 'recharts';
import { useBoardChartData } from '../../hooks/useBoardChartData';
import { BaseChartProps } from './types';
import { CHART_STYLES, getCurrencySymbol, formatCurrency } from './chartTheme';
import { useSetChartMetadata } from './ChartContext';

const REFUND_CATEGORIES = new Set([
    'product_return',
    'partial_return',
    'shipping_refund',
    'refund_adjustment',
    'tax_refund',
    'net_return',
    'return',
]);

const REFUND_COLORS = [
    '#EF4444', // Red-500
    '#F97316', // Orange-500
    '#F59E0B', // Amber-500
    '#DC2626', // Red-600
    '#FB923C', // Orange-400
];

const CATEGORY_LABELS: Record<string, string> = {
    product_return: 'Product Return',
    partial_return: 'Partial Return',
    shipping_refund: 'Shipping Refund',
    refund_adjustment: 'Refund Adjustment',
    tax_refund: 'Tax Refund',
};

interface RefundSegment {
    name: string;
    value: number;
}

export const RefundTypeBreakdownChart: React.FC<BaseChartProps> = () => {
    const { t } = useTranslation();
    const { finance, currencySettings } = useBoardChartData();

    const currencySymbol = useMemo(
        () => getCurrencySymbol(currencySettings?.default),
        [currencySettings?.default]
    );

    const chartData = useMemo((): RefundSegment[] => {
        if (!finance?.breakdown) return [];

        const byCategory: Record<string, number> = {};

        for (const item of finance.breakdown) {
            if (item.type === 'EXPENSE' && REFUND_CATEGORIES.has(item.category as string)) {
                const cat = item.category as string;
                byCategory[cat] = (byCategory[cat] || 0) + (Number(item.value) || 0);
            }
        }

        return Object.entries(byCategory)
            .filter(([, value]) => value > 0)
            .sort(([, a], [, b]) => b - a)
            .map(([category, value]) => ({
                name: t(`charts.refundCategory.${category}`, CATEGORY_LABELS[category] || category),
                value,
            }));
    }, [finance?.breakdown, t]);

    const isEmpty = useMemo(() => chartData.length === 0, [chartData]);

    useSetChartMetadata({ isEmpty });

    if (isEmpty) return null;

    return (
        <div className="w-full h-full bg-transparent overflow-hidden flex flex-col">
            <div className="flex-1 min-h-0 p-2">
                <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                        <Pie
                            data={chartData}
                            cx="50%"
                            cy="50%"
                            innerRadius="40%"
                            outerRadius="70%"
                            paddingAngle={2}
                            dataKey="value"
                            nameKey="name"
                            stroke="none"
                        >
                            {chartData.map((_, index) => (
                                <Cell
                                    key={`cell-${index}`}
                                    fill={REFUND_COLORS[index % REFUND_COLORS.length]}
                                />
                            ))}
                        </Pie>
                        <Tooltip
                            contentStyle={CHART_STYLES.tooltip.contentStyle}
                            itemStyle={CHART_STYLES.tooltip.itemStyle}
                            formatter={(value: number, name: string) => [
                                formatCurrency(value, currencySymbol),
                                name,
                            ]}
                        />
                        <Legend wrapperStyle={{ fontSize: '10px' }} />
                    </PieChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};
